import React, {useEffect, useMemo, useState} from "react"
import {useParams, useSearchParams} from "react-router-dom"
import {Badge, Box, IconButton, Tooltip} from "@mui/material"
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined"
import {useGetAiDialogsQuery} from "../../core/api/ai-zendesk-api"
import {ALL_GAMES, hermesGameToAiGame} from "./aiTicketsUtils"
import TicketList from "./TicketList"
import TicketDialogView from "./TicketDialogView"

const PENDING_POLL_MS = 20000
const TICKET_PARAM = "ticketId"

/**
 * Panel body: collapsed -> badge with the number of dialogs awaiting approval;
 * expanded -> ticket list or the selected ticket's dialog.
 *
 * `?ticketId=N` in the URL opens the panel straight on that ticket (deep link from Zendesk).
 * Selecting a ticket writes the param back so the state is shareable.
 */
export default function AiTicketsPanel({open, onOpen}) {
    const {game} = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const urlTicketId = searchParams.get(TICKET_PARAM)

    const [selectedTicketId, setSelectedTicketId] = useState(urlTicketId)
    // the game comes from the header (route), sections without an ai-zendesk game show everything
    const gameFilter = hermesGameToAiGame(game) ?? ALL_GAMES
    const gameParam = gameFilter === ALL_GAMES ? undefined : gameFilter

    // deep link: ?ticketId=N forces the panel open on that ticket
    useEffect(() => {
        if (urlTicketId) {
            setSelectedTicketId(urlTicketId)
            onOpen()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlTicketId])

    // pending queue is polled even when collapsed — it feeds the badge
    const [pendingPoll, setPendingPoll] = useState(PENDING_POLL_MS)
    const pendingQuery = useGetAiDialogsQuery({hasPendingAnswers: true, game: gameParam}, {pollingInterval: pendingPoll})
    useEffect(() => {
        // stop hammering (and toasting) while ai-zendesk is unreachable; manual refresh re-enables
        setPendingPoll(pendingQuery.isError ? 0 : PENDING_POLL_MS)
    }, [pendingQuery.isError])

    // server already filters by game; the client-side filter only guards against stale cache entries
    const pendingDialogs = useMemo(() => {
        const all = Array.isArray(pendingQuery.data) ? pendingQuery.data : []
        return gameFilter === ALL_GAMES ? all : all.filter((dialog) => dialog.game === gameFilter)
    }, [pendingQuery.data, gameFilter])

    const selectTicket = (ticketId) => {
        setSelectedTicketId(ticketId)
        const next = new URLSearchParams(searchParams)
        if (ticketId) next.set(TICKET_PARAM, ticketId)
        else next.delete(TICKET_PARAM)
        setSearchParams(next, {replace: true})
    }

    if (!open) {
        const count = pendingDialogs.length
        const title = pendingQuery.isError
            ? "AI tickets: failed to load"
            : `${count} AI answer${count === 1 ? "" : "s"} awaiting approval`
        return (
            <Box sx={{display: "flex", justifyContent: "center", pt: 1}}>
                <Tooltip title={title} placement="left">
                    <IconButton onClick={onOpen} aria-label={title}>
                        <Badge
                            badgeContent={count}
                            color={pendingQuery.isError ? "default" : "error"}
                            max={99}
                            overlap="circular"
                        >
                            <SmartToyOutlinedIcon color={count > 0 ? "primary" : "action"}/>
                        </Badge>
                    </IconButton>
                </Tooltip>
            </Box>
        )
    }

    // Drawer forces whiteSpace: nowrap — reset it for real content
    return (
        <Box sx={{display: "flex", flexDirection: "column", flex: 1, minHeight: 0, width: "100%", maxWidth: "100%", boxSizing: "border-box", whiteSpace: "normal"}}>
            {selectedTicketId
                ? <TicketDialogView ticketId={selectedTicketId} onBack={() => selectTicket(null)}/>
                : <TicketList
                    gameFilter={gameFilter}
                    pendingQuery={pendingQuery}
                    pendingDialogs={pendingDialogs}
                    onRetry={() => {
                        setPendingPoll(PENDING_POLL_MS)
                        pendingQuery.refetch()
                    }}
                    onSelect={selectTicket}
                />}
        </Box>
    )
}
